import React, { useContext } from 'react'
import GeoContext from 'contexts/geoContext'
import useBedbankRates from 'hooks/useBedbankRates'
import useBedbankOfferSell from 'hooks/useBedbankOfferSell'
import { getDisplayTotalPrice } from 'lib/hotels/bedbankUtils'
import PriceRowPrice from 'components/Luxkit/PricePoints/PriceRowPrice'
import OfferPageStickyFooter from './OfferPageStickyFooter'
import WalledContent from 'components/Common/WalledContent'
import Group from 'components/utils/Group'
import PriceRowPriceCaption from 'components/Luxkit/PricePoints/PriceRowPriceCaption'
import { pluralizeToString } from 'lib/string/pluralize'

interface Props {
  walledGarden: boolean;
  duration: number;
  offer: App.BedbankOffer;
  rooms: Array<App.Occupants>;
  checkinDate: moment.Moment;
  checkoutDate: moment.Moment;
  flightPrice: number;
  flightsEnabled: boolean;
  onScrollTo: () => void;
}

function BedbankOfferPageStickyFooter(props: Props) {
  const {
    walledGarden,
    offer,
    rooms,
    checkinDate,
    checkoutDate,
    duration,
    flightPrice,
    flightsEnabled,
    onScrollTo,
  } = props

  const { isTaxExclusiveRegion } = useContext(GeoContext)
  const [rates, fetching] = useBedbankRates(offer.id, rooms, checkinDate, checkoutDate)

  const sell = useBedbankOfferSell(offer, duration, rooms)

  const hotelPrice = rates[0] ? (isTaxExclusiveRegion ? rates[0].totals.exclusive : rates[0].totals.inclusive) : sell?.price ?? 0
  const hotelNights = rates[0]?.nights ?? sell?.los
  const propertyFees = rates[0]?.totals?.propertyFees ?? sell?.propertyFees ?? 0

  // Do not show any price details if there are no active promotions
  // Offer promotions only contain active promotions
  const hasActivePromo = offer.promotions && offer.promotions.length > 0
  const showSummary = !!((checkinDate && checkoutDate && rates[0]) || (hasActivePromo && !checkinDate && !checkoutDate && sell))
  const saleUnit = offer.displayPricingAsPerPerson ? 'person' : 'room'
  const datesSelected = !!(checkinDate && checkoutDate)

  const promoteAsBundle = flightsEnabled && !!flightPrice && offer.promoteAsBundle

  const totalPrices = getDisplayTotalPrice({
    hotelPrice: isTaxExclusiveRegion ? hotelPrice : hotelPrice + propertyFees,
    flightPrice,
    checkIn: checkinDate,
    rooms,
    displayPricingAsPerPerson: offer.displayPricingAsPerPerson,
  })

  const offerPrice = totalPrices.totals.price + (promoteAsBundle ? totalPrices.totals.flight : 0)

  const isSoldOut = rates.length == 0 && datesSelected

  if (isSoldOut) {
    return null
  }

  return (
    <OfferPageStickyFooter callToActionCopy="View options" onCTAClick={onScrollTo} walledGarden={walledGarden}>
      <WalledContent enforced={walledGarden} >
        {showSummary && !fetching &&
          <Group direction="vertical">
            <PriceRowPriceCaption>
              {pluralizeToString('night', hotelNights)}{promoteAsBundle && ' + flights'} from
            </PriceRowPriceCaption>
            <PriceRowPrice
              size="S"
              price={offerPrice}
              saleUnit={saleUnit}
              taxesAndFees={sell?.taxesAndFees ?? 'inclusive'}
            />
          </Group>
        }
      </WalledContent>
    </OfferPageStickyFooter>
  )
}

export default BedbankOfferPageStickyFooter
